<template>
  <div class="fin-page" :class="{ 'fin-page--customer': channel === 'customer' }">
      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#00AEEF" size="48" />
        <p>{{ loadingMessage }}</p>
      </div>

      <div v-else class="success-card">
        <div class="success-icon" :class="{ pending: issuancePending }">
          <v-icon size="48" color="white">{{ issuancePending ? 'mdi-clock-outline' : 'mdi-check-circle' }}</v-icon>
        </div>
        <h1>{{ headline }}</h1>
        <p class="sub">{{ completionSubtext }}</p>

        <div class="details-grid">
          <div class="detail-item">
            <label>Policy number</label>
            <p>{{ displayPolicyNumber }}</p>
          </div>
          <div v-if="!paConfig.hideVerifiedName" class="detail-item">
            <label>Proposer</label>
            <p>{{ quote.proposerName || '—' }}</p>
          </div>
          <div class="detail-item">
            <label>National ID</label>
            <p>{{ quote.nationalId || '—' }}</p>
          </div>
          <div class="detail-item">
            <label>Cover</label>
            <p>{{ quote.optionName }} · {{ durationLabel }}</p>
          </div>
          <div class="detail-item">
            <label>Premium paid</label>
            <p class="premium">KES {{ formatMoney(quote.premium) }}</p>
          </div>
          <div class="detail-item">
            <label>Quote reference</label>
            <p class="mono">{{ quoteId }}</p>
          </div>
        </div>

        <policy-documents-panel
          v-if="displayPolicyNumber && displayPolicyNumber !== '—'"
          :policy-number="displayPolicyNumber"
          :policy-document="policyDocument"
          :loading="docLoading"
          :error="docError"
          :loading-message="docLoadingMessage"
          :waiting-message="docWaitingMessage"
          compact
          @retry="checkPolicyDocument"
        />

        <div class="actions">
          <button class="btn-outline" @click="$router.push({ name: paConfig.routes.dashboard })">
            <v-icon small class="tw-mr-2">mdi-view-dashboard</v-icon>
            Dashboard
          </button>
          <button v-if="displayPolicyNumber && displayPolicyNumber !== '—'" class="btn-outline" @click="checkPolicyDocument">
            <v-icon small class="tw-mr-2">mdi-refresh</v-icon>
            Refresh document
          </button>
          <button class="btn-primary" @click="startAnother">
            <v-icon small color="white" class="tw-mr-2">mdi-plus</v-icon>
            {{ paConfig.channel === 'customer' ? 'Buy another cover' : 'New PA quote' }}
          </button>
        </div>
      </div>
  </div>
</template>

<script>
import { PA_COURSE_DURATIONS } from '@/constants/paStudentsRateCard';
import PolicyDocumentsPanel from '@/components/pa/PolicyDocumentsPanel';
import { getPaQuotation, isAgentPaMockEnabled } from '@/services/agentPaApi';
import { clearPaQuoteCache } from '@/utils/saleCache';
import { getPaChannelConfig, PA_JOURNEY_DIRECT } from '@/utils/paChannel';
import { pollPolicyDocument, resolvePaPolicyDocument } from '@/utils/policyDocumentsApi';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

const ISSUANCE_POLL_MS = 5000;
const ISSUANCE_MAX_ATTEMPTS = 24;
const DOC_POLL_MS = 5000;
const DOC_MAX_ATTEMPTS = 24;

export default {
  name: 'PaFinalizationPage',
  components: { PolicyDocumentsPanel },
  props: {
    quoteId: { type: [String, Number], required: true },
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
  },
  data() {
    return {
      loading: true,
      quote: {},
      issuancePending: false,
      loadingMessage: 'Loading policy details…',
      docLoading: false,
      docError: '',
      docLoadingMessage: 'Checking for policy document…',
      policyDocument: null,
      pollTimer: null,
      docPollTimer: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    paConfig() {
      return getPaChannelConfig({ paChannel: this.channel });
    },
    displayPolicyNumber() {
      const num = this.quote.policyNumber || this.quote.policyNo;
      return num || (this.issuancePending ? 'Pending…' : '—');
    },
    headline() {
      if (this.issuancePending) return 'Payment received';
      return 'Policy issued successfully';
    },
    completionSubtext() {
      if (isAgentPaMockEnabled()) {
        return 'Students Personal Accident cover is now active (demo).';
      }
      if (this.issuancePending) {
        return 'Your policy is being issued. This usually takes a minute — the policy document will appear here when ready.';
      }
      if (this.policyDocument) {
        return 'Your policy document is ready. A copy is also sent to email.';
      }
      return 'Policy issued. Your policy document is being prepared and will also be emailed when ready.';
    },
    docWaitingMessage() {
      return 'Policy document is being prepared. It is also sent to email when ready.';
    },
    durationLabel() {
      const d = PA_COURSE_DURATIONS.find((x) => x.code === this.quote.courseDuration);
      return d?.label || '';
    },
  },
  async mounted() {
    if (this.paConfig.trackAnalytics) {
      trackAgentInteraction(this, 'pa_finalization_view', { quoteId: this.quoteId });
    }
    await this.loadQuoteAndPoll();
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    clearTimers() {
      if (this.pollTimer) clearTimeout(this.pollTimer);
      if (this.docPollTimer) clearTimeout(this.docPollTimer);
    },
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    startAnother() {
      clearPaQuoteCache(this.authenticatedUser?.email, this.quoteId);
      const query = this.paConfig.channel === 'agent' ? { journey: PA_JOURNEY_DIRECT } : {};
      this.$router.push({ name: this.paConfig.routes.planSelect, query });
    },
    async loadQuoteAndPoll() {
      this.loading = true;
      const email = this.authenticatedUser?.email;
      clearPaQuoteCache(email, this.quoteId);
      let q = {};
      const res = await getPaQuotation(this.quoteId, this.channel);
      if (res.success) q = { ...q, ...res.data };
      this.quote = q || {};
      this.loading = false;

      const policyNumber = this.quote.policyNumber || this.quote.policyNo;
      if (policyNumber) {
        await this.checkPolicyDocument(true);
        return;
      }
      if (isAgentPaMockEnabled()) {
        return;
      }
      this.issuancePending = true;
      this.pollForIssuance(1);
    },
    pollForIssuance(attempt) {
      if (attempt > ISSUANCE_MAX_ATTEMPTS) {
        this.issuancePending = true;
        return;
      }
      this.pollTimer = setTimeout(async () => {
        const res = await getPaQuotation(this.quoteId, this.channel);
        if (res.success) {
          this.quote = { ...this.quote, ...res.data };
          const policyNumber = this.quote.policyNumber || this.quote.policyNo;
          if (policyNumber) {
            this.issuancePending = false;
            await this.checkPolicyDocument(true);
            return;
          }
        }
        this.pollForIssuance(attempt + 1);
      }, ISSUANCE_POLL_MS);
    },
    async checkPolicyDocument(autoPoll = false) {
      const policyNumber = this.quote.policyNumber || this.quote.policyNo;
      if (!policyNumber) return;

      this.docLoading = true;
      this.docError = '';
      this.docLoadingMessage = autoPoll
        ? 'Waiting for policy document…'
        : 'Checking for policy document…';

      try {
        if (autoPoll && !isAgentPaMockEnabled()) {
          const result = await pollPolicyDocument(policyNumber, {
            intervalMs: DOC_POLL_MS,
            maxAttempts: DOC_MAX_ATTEMPTS,
            onAttempt: (attempt, max) => {
              this.docLoadingMessage = `Waiting for policy document… (${attempt}/${max})`;
            },
          });
          this.policyDocument = result.document;
        } else {
          const result = await resolvePaPolicyDocument(policyNumber);
          if (result.error) this.docError = result.error;
          this.policyDocument = result.document;
        }
      } finally {
        this.docLoading = false;
      }
    },
  },
  metaInfo: { title: 'Students PA — Complete' },
};
</script>

<style scoped>
.fin-page { max-width: 640px; margin: 0 auto; padding: 32px 24px; }
.loading-wrap { text-align: center; padding: 48px; color: #64748B; }
.success-card {
  background: white; border-radius: 16px; padding: 32px 28px;
  box-shadow: 0 4px 24px rgba(24,70,121,0.1); border: 1px solid #E8EDF2; text-align: center;
}
.success-icon {
  width: 72px; height: 72px; border-radius: 50%; background: #16A34A;
  display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;
}
.success-icon.pending { background: #F59E0B; }
.success-card h1 { font-size: 1.4rem; color: #1E4276; margin: 0 0 8px; }
.sub { color: #64748B; font-size: 0.9rem; margin: 0 0 24px; }
.details-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; text-align: left;
  background: #F8FAFC; border-radius: 12px; padding: 20px; margin-bottom: 24px;
}
.detail-item label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: #94A3B8; font-weight: 600; }
.detail-item p { margin: 4px 0 0; font-weight: 600; color: #1E4276; font-size: 0.9rem; }
.detail-item .premium { color: #184679; font-size: 1.1rem; }
.detail-item .mono { font-family: monospace; font-size: 0.75rem; word-break: break-all; }
.actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.btn-outline, .btn-primary {
  display: inline-flex; align-items: center; padding: 12px 20px; border-radius: 10px;
  font-weight: 700; cursor: pointer; border: none;
}
.btn-outline { background: white; border: 1px solid #CBD5E1; color: #1E4276; }
.btn-primary { background: #00AEEF; color: white; }
@media (max-width: 500px) {
  .fin-page { padding: 16px 12px 88px; }
  .success-card { padding: 22px 16px; border-radius: 14px; }
  .success-card h1 { font-size: 1.2rem; }
  .sub { font-size: 0.82rem; }
  .details-grid {
    grid-template-columns: 1fr;
    padding: 14px;
    gap: 12px;
  }
  .actions {
    flex-direction: column;
    align-items: stretch;
  }
  .actions .btn-outline,
  .actions .btn-primary {
    width: 100%;
    justify-content: center;
    min-height: 46px;
  }
}
</style>
